import React from 'react';
import axios from 'axios';
import { APP_STATIC_DATA } from './AppStaticData';
import { cacheBustMediaUrl } from './cacheBustMediaUrl';

function biHasContent(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  const en = value.en != null && String(value.en).trim().length > 0;
  const ar = value.ar != null && String(value.ar).trim().length > 0;
  return en || ar;
}

function padCalendar2(n) {
  return String(n).padStart(2, '0');
}

/** Google Calendar `dates` param: UTC compact form `YYYYMMDDTHHmmssZ`. */
function formatGoogleCalendarUtc(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return (
    date.getUTCFullYear() +
    padCalendar2(date.getUTCMonth() + 1) +
    padCalendar2(date.getUTCDate()) +
    'T' +
    padCalendar2(date.getUTCHours()) +
    padCalendar2(date.getUTCMinutes()) +
    padCalendar2(date.getUTCSeconds()) +
    'Z'
  );
}

function escapeIcsText(text) {
  if (text == null) return '';
  return String(text)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

function buildGoogleCalendarUrl({ title, details, location, start, end }) {
  const dates = `${formatGoogleCalendarUtc(start)}/${formatGoogleCalendarUtc(end)}`;
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: title || 'Event',
    dates,
    details: details || '',
    location: location || '',
  });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

const DEFAULT_SLIDE_MEDIA = {
  slide1: { type: 'image', src: '/img/Page1.jpg' },
  slide2: { type: 'image', src: '/img/Page2.jpg' },
  slide3: { type: 'image', src: '/img/Page3.jpg' },
  slide4: { type: 'image', src: '/img/Page4.jpg' },
  slide5: { type: 'image', src: '/img/Page5.jpg' },
  slide6: { type: 'image', src: '/img/Page6.jpg' },
};

/** Same clamp() values as #slide2 --s2-quote-* in index.css (inline beats any stray overrides). */
const SLIDE2_MQ_FONT = {
  ltrBody: 'clamp(0.92rem, 3.15vw, 1.14rem)',
  ltrName: 'clamp(1rem, 3.45vw, 1.22rem)',
  rtlBody: 'clamp(1.12rem, 4.1vw, 1.48rem)',
  rtlName: 'clamp(1.28rem, 4.65vw, 1.72rem)',
  rtlLast: 'clamp(1.06rem, 3.95vw, 1.4rem)',
};

/** Keep in sync with #slide2 --s2-en-with-ar-mq-scale in index.css */
const SLIDE2_EN_WITH_AR_MQ_SCALE = 1.15;

/** Keep in sync with #slide2 --s2-en-couple-with-ar-mq-scale (husband/bride/& vs ceremony text). */
const SLIDE2_EN_COUPLE_WITH_AR_MQ_SCALE = 1.3;

function slide2FontWithArQuotesOptical(mqLang, fieldLang, clampExpr) {
  if (mqLang === 'ar' && fieldLang === 'en') {
    return `calc(${clampExpr} * ${SLIDE2_EN_WITH_AR_MQ_SCALE})`;
  }
  return clampExpr;
}

/** Smaller than marriageQuotes (bible unchanged). */
const SLIDE2_EVANGELIST_FONT = {
  ltr: 'clamp(0.76rem, 2.55vw, 0.92rem)',
  rtl: 'clamp(0.88rem, 3.05vw, 1.08rem)',
};

/** Defaults match index.css envelope look; override via AppData.ui.envelopeFeel.colors */
const ENVELOPE_FEEL_COLOR_DEFAULTS = {
  paper1: '#5c4430',
  paper2: '#785c44',
  paper3: '#4a3426',
  paperAlpha1: 0.52,
  paperAlpha2: 0.46,
  paperAlpha3: 0.55,
  flapSide1: '#87694e',
  flapSide2: '#5f4634',
  flapSideAlpha1: 0.58,
  flapSideAlpha2: 0.62,
  flapBottom1: '#6e523c',
  flapBottom2: '#553e2e',
  flapBottomAlpha1: 0.6,
  flapBottomAlpha2: 0.64,
  flapTop1: '#967658',
  flapTop2: '#694e3a',
  flapTopAlpha1: 0.62,
  flapTopAlpha2: 0.58,
  kraftInsetShadow: 'rgba(30, 18, 10, 0.28)',
  sealHighlight: 'rgba(110, 48, 52, 0.45)',
  sealMid: '#5c2428',
  sealDeep: '#451a20',
  sealInk: '#2d1014',
  sealVoid: '#160809',
  sealEdge: 'rgba(60, 28, 32, 0.5)',
  sealRim: 'rgba(25, 10, 12, 0.65)',
  sealInsetTop: 'rgba(95, 42, 48, 0.35)',
  sealInsetBottom: 'rgba(8, 2, 4, 0.75)',
  sealInsetHighlight: 'rgba(130, 72, 78, 0.12)',
  sealRing: 'rgba(140, 112, 62, 0.42)',
  sealShine: 'rgba(200, 150, 145, 0.22)',
  textWelcome: '#f5ebe0',
  textGuest: '#edd6c9',
  textLine: '#e8dccf',
  textLineLead: '#f0d6d8',
  textHint: 'rgba(232, 220, 207, 0.72)',
  ornament: 'rgba(45, 28, 18, 0.65)',
};

function parseHexToRgb(hex) {
  if (hex == null || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function envelopePaint(defaults, colors, key, alphaKey) {
  const raw =
    colors && colors[key] != null && String(colors[key]).trim() !== ''
      ? String(colors[key]).trim()
      : defaults[key];
  const s = String(raw).trim();
  if (s.startsWith('rgba') || s.startsWith('rgb(')) return s;
  const rgb = parseHexToRgb(s.startsWith('#') ? s : `#${s}`);
  if (!rgb) return s;
  const aSrc = colors && colors[alphaKey] != null ? colors[alphaKey] : defaults[alphaKey];
  const n = Number(aSrc);
  const a = Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

function envelopePickSolid(colors, key, defaults) {
  const raw =
    colors && colors[key] != null && String(colors[key]).trim() !== ''
      ? String(colors[key]).trim()
      : defaults[key];
  const s = String(raw).trim();
  if (s.startsWith('rgba') || s.startsWith('rgb(') || s.startsWith('#')) return s;
  return `#${s.replace(/^#/, '')}`;
}

function buildEnvelopeFeelTheme(cfg) {
  const c = (cfg && cfg.colors) || {};
  const d = ENVELOPE_FEEL_COLOR_DEFAULTS;
  const k1 = envelopePaint(d, c, 'paper1', 'paperAlpha1');
  const k2 = envelopePaint(d, c, 'paper2', 'paperAlpha2');
  const k3 = envelopePaint(d, c, 'paper3', 'paperAlpha3');
  const fs1 = envelopePaint(d, c, 'flapSide1', 'flapSideAlpha1');
  const fs2 = envelopePaint(d, c, 'flapSide2', 'flapSideAlpha2');
  const fb1 = envelopePaint(d, c, 'flapBottom1', 'flapBottomAlpha1');
  const fb2 = envelopePaint(d, c, 'flapBottom2', 'flapBottomAlpha2');
  const ft1 = envelopePaint(d, c, 'flapTop1', 'flapTopAlpha1');
  const ft2 = envelopePaint(d, c, 'flapTop2', 'flapTopAlpha2');
  const kraftInset =
    c.kraftInsetShadow != null && String(c.kraftInsetShadow).trim() !== ''
      ? String(c.kraftInsetShadow).trim()
      : d.kraftInsetShadow;
  const sealHighlight =
    c.sealHighlight != null && String(c.sealHighlight).trim() !== ''
      ? String(c.sealHighlight).trim()
      : d.sealHighlight;
  const sealMid = envelopePickSolid(c, 'sealMid', d);
  const sealDeep = envelopePickSolid(c, 'sealDeep', d);
  const sealInk = envelopePickSolid(c, 'sealInk', d);
  const sealVoid = envelopePickSolid(c, 'sealVoid', d);
  const sealEdge =
    c.sealEdge != null && String(c.sealEdge).trim() !== '' ? String(c.sealEdge).trim() : d.sealEdge;
  const sealRim =
    c.sealRim != null && String(c.sealRim).trim() !== '' ? String(c.sealRim).trim() : d.sealRim;
  const sealInsetTop =
    c.sealInsetTop != null && String(c.sealInsetTop).trim() !== ''
      ? String(c.sealInsetTop).trim()
      : d.sealInsetTop;
  const sealInsetBottom =
    c.sealInsetBottom != null && String(c.sealInsetBottom).trim() !== ''
      ? String(c.sealInsetBottom).trim()
      : d.sealInsetBottom;
  const sealInsetHighlight =
    c.sealInsetHighlight != null && String(c.sealInsetHighlight).trim() !== ''
      ? String(c.sealInsetHighlight).trim()
      : d.sealInsetHighlight;
  const sealRing =
    c.sealRing != null && String(c.sealRing).trim() !== '' ? String(c.sealRing).trim() : d.sealRing;
  const sealShine =
    c.sealShine != null && String(c.sealShine).trim() !== '' ? String(c.sealShine).trim() : d.sealShine;
  const textWelcome =
    c.textWelcome != null && String(c.textWelcome).trim() !== ''
      ? String(c.textWelcome).trim()
      : d.textWelcome;
  const textGuest =
    c.textGuest != null && String(c.textGuest).trim() !== '' ? String(c.textGuest).trim() : d.textGuest;
  const textLine =
    c.textLine != null && String(c.textLine).trim() !== '' ? String(c.textLine).trim() : d.textLine;
  const textLineLead =
    c.textLineLead != null && String(c.textLineLead).trim() !== ''
      ? String(c.textLineLead).trim()
      : d.textLineLead;
  const textHint =
    c.textHint != null && String(c.textHint).trim() !== '' ? String(c.textHint).trim() : d.textHint;
  const ornament =
    c.ornament != null && String(c.ornament).trim() !== '' ? String(c.ornament).trim() : d.ornament;

  return {
    '--env-kraft-1': k1,
    '--env-kraft-2': k2,
    '--env-kraft-3': k3,
    '--env-flap-side-1': fs1,
    '--env-flap-side-2': fs2,
    '--env-flap-bottom-1': fb1,
    '--env-flap-bottom-2': fb2,
    '--env-flap-top-1': ft1,
    '--env-flap-top-2': ft2,
    '--env-kraft-inset': kraftInset,
    '--seal-highlight': sealHighlight,
    '--seal-mid': sealMid,
    '--seal-deep': sealDeep,
    '--seal-ink': sealInk,
    '--seal-void': sealVoid,
    '--seal-edge': sealEdge,
    '--seal-rim': sealRim,
    '--seal-inset-top': sealInsetTop,
    '--seal-inset-bottom': sealInsetBottom,
    '--seal-inset-highlight': sealInsetHighlight,
    '--seal-ring': sealRing,
    '--seal-shine': sealShine,
    '--env-text-welcome': textWelcome,
    '--env-text-guest': textGuest,
    '--env-text-line': textLine,
    '--env-text-line-lead': textLineLead,
    '--env-text-hint': textHint,
    '--env-ornament': ornament,
  };
}

function buildCurtainFeelTheme(cfg) {
  const c = (cfg && cfg.colors) || {};
  const fabric1 = c.fabric1 || 'rgba(120, 18, 28, 0.92)';
  const fabric2 = c.fabric2 || 'rgba(55, 8, 14, 0.94)';
  const fold = c.fold || 'rgba(255, 255, 255, 0.08)';
  const shadow = c.shadow || 'rgba(0, 0, 0, 0.58)';
  return {
    '--curtain-fabric-1': fabric1,
    '--curtain-fabric-2': fabric2,
    '--curtain-fold': fold,
    '--curtain-shadow': shadow,
  };
}

class MainAppContainer extends React.Component {

  constructor() {
    super();
    this.musicRef = React.createRef();
    this.swipeStripRef = React.createRef();
    this.stripPointerDrag = null;
    this._stripWheelHandler = null;
    this.state = {
      guestID: "",
      guestPhoneNumber: "",
      invitationTo: "",
      wtpsMsg: "",
      isVideoMuted: true,
      nbrOfInvited: "",
      didstart: false,
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      listOfInv: [],

      AppData: APP_STATIC_DATA,
      apiCall: "https://wed.techynnovative.com/WedInviteBackEnd//v1/api/get_data",
      suffix : "Toufic", // Dynamic table name,
      displayLastPage: false,
      lastPageUserFireworks: [],
      lastPageHintDismissed: false,
      startPageReveal: false,
      swipeViewsOpaque: true,
      leafInstances: [],
      scratchRevealed: { days: false, hours: false, minutes: false, seconds: false },
    };
    this._startRevealTimer = null;
    this._stripBookFlipScrollHandler = null;
    this._stripBookFlipResizeHandler = null;
    this._stripScrollFlipScheduled = false;
    this._leafResizeHandler = null;
    this._scratchCheckScheduled = false;
    this._scratchPointer = null;
    this._scratchCanvases = { days: null, hours: null, minutes: null, seconds: null };
  }

  getLeavesLive = () => {
    const ui = this.state.AppData && this.state.AppData.ui;
    const l = ui && ui.leaves;
    if (!l || l.enabled !== true) return null;
    return l;
  };

  initLeaves = () => {
    const cfg = this.getLeavesLive();
    if (!cfg) {
      this.setState({ leafInstances: [] });
      return;
    }
    let count = Number(cfg.count);
    if (!Number.isFinite(count)) count = 18;
    count = Math.min(60, Math.max(1, Math.floor(count)));

    const imgsRaw = (cfg.images && cfg.images.length ? cfg.images : ['/img/realLeaf1.png', '/img/realLeaf2.png', '/img/realLeaf3.png', '/img/realLeaf4.png']).map(String);
    const imgs = imgsRaw.map((u) => this.mediaUrl(u));
    const spins = ['clockwiseSpin', 'counterclockwiseSpinAndFlip'];

    // Stable-ish randomness per session
    const base = Date.now() % 100000;
    const rand = (i) => {
      const x = Math.sin((base + i * 9973) * 0.001) * 10000;
      return x - Math.floor(x);
    };

    const next = Array.from({ length: count }, (_, i) => {
      const r1 = rand(i + 1);
      const r2 = rand(i + 19);
      const r3 = rand(i + 37);
      const leftVw = 2 + r1 * 96; // 2..98
      const fallDur = 20 + r2 * 20; // 20..40s
      const fallDelay = r3 * 1.2; // 0..1.2s
      const img = imgs[i % imgs.length];
      const spinName = spins[i % spins.length];
      const spinDur = 11 + rand(i + 71) * 9; // 11..20s
      return { id: `leaf-${i}`, leftVw, fallDur, fallDelay, img, spinName, spinDur };
    });

    this.setState({ leafInstances: next }, () => this.normalizeLeafPositions());
  };

  /** Vertical single column (optional); when on, horizontal book swipe is off. */
  getScrollSinglePageLive = () => {
    const s = this.state.AppData && this.state.AppData.scrollSinglePage;
    if (!s || s.enabled !== true) return null;
    return s;
  };

  getCalendarAddLive = () => {
    const c = this.state.AppData && this.state.AppData.calendarAdd;
    if (!c || c.enabled !== true) return null;
    return c;
  };

  getSwipeNavFooterText = () => {
    const scroll = this.getScrollSinglePageLive();
    if (scroll && scroll.scrollHint != null && (scroll.scrollHint.en != null || scroll.scrollHint.ar != null)) {
      const t = String(this.resolveText(scroll.scrollHint) || '').trim();
      if (t) return t;
    }
    const ui = this.state.AppData.ui || {};
    return this.getUiLine(ui.swipeLeft);
  };

  buildCeremonyCalendarWindow = () => {
    const AppData = this.state.AppData;
    const settings = AppData.settings || {};
    const ceremony = AppData.ceremony || {};
    const cfg = this.getCalendarAddLive();
    if (!cfg) return null;
    const start = new Date(settings.countdownTargetIso);
    if (Number.isNaN(start.getTime())) return null;
    let dur = Number(cfg.ceremonyDurationMinutes);
    if (!Number.isFinite(dur)) dur = 90;
    dur = Math.min(24 * 60, Math.max(15, dur));
    const end = new Date(start.getTime() + dur * 60000);
    let title = '';
    if (cfg.ceremonyTitle && (cfg.ceremonyTitle.en != null || cfg.ceremonyTitle.ar != null)) {
      title = String(this.resolveText(cfg.ceremonyTitle) || '').trim();
    }
    if (!title) {
      const h = String(this.resolveText(AppData.husband) || '').trim();
      const b = String(this.resolveText(AppData.bride) || '').trim();
      const ct = String(this.resolveText(ceremony.title) || '').trim() || 'Wedding ceremony';
      title = h && b ? `${h} & ${b} — ${ct}` : ct;
    }
    let details = '';
    if (cfg.ceremonyDescription && (cfg.ceremonyDescription.en != null || cfg.ceremonyDescription.ar != null)) {
      details = String(this.resolveText(cfg.ceremonyDescription) || '').trim();
    }
    if (!details) {
      details = [this.resolveText(ceremony.dateLine), this.resolveText(ceremony.time)]
        .filter((x) => x && String(x).trim())
        .join('\n');
    }
    const church = String(this.resolveText(ceremony.church) || '').trim();
    const loc = String(this.resolveText(ceremony.location) || '').trim();
    const location = [church, loc].filter(Boolean).join(', ');
    return { start, end, title, details, location };
  };

  buildDinnerCalendarWindow = () => {
    const cfg = this.getCalendarAddLive();
    if (!cfg || cfg.includeDinner === false) return null;
    const AppData = this.state.AppData;
    const dinner = AppData.dinner;
    if (!dinner || !biHasContent(dinner.dinnerTitle)) return null;
    const settings = AppData.settings || {};
    const ceremonyStart = new Date(settings.countdownTargetIso);
    if (Number.isNaN(ceremonyStart.getTime())) return null;
    let start;
    if (cfg.dinnerStartIso != null && String(cfg.dinnerStartIso).trim() !== '') {
      start = new Date(cfg.dinnerStartIso);
    } else {
      let off = Number(cfg.dinnerOffsetMinutesFromCeremonyStart);
      if (!Number.isFinite(off)) off = 60;
      off = Math.min(12 * 60, Math.max(0, off));
      start = new Date(ceremonyStart.getTime() + off * 60000);
    }
    if (Number.isNaN(start.getTime())) return null;
    let dur = Number(cfg.dinnerDurationMinutes);
    if (!Number.isFinite(dur)) dur = 120;
    dur = Math.min(24 * 60, Math.max(30, dur));
    const end = new Date(start.getTime() + dur * 60000);
    let title = '';
    if (cfg.dinnerTitle && (cfg.dinnerTitle.en != null || cfg.dinnerTitle.ar != null)) {
      title = String(this.resolveText(cfg.dinnerTitle) || '').trim();
    }
    if (!title) {
      const h = String(this.resolveText(AppData.husband) || '').trim();
      const b = String(this.resolveText(AppData.bride) || '').trim();
      const dt = String(this.resolveText(dinner.dinnerTitle) || '').trim();
      title = [h && b ? `${h} & ${b}` : '', dt].filter(Boolean).join(' — ');
    }
    let details = '';
    if (cfg.dinnerDescription && (cfg.dinnerDescription.en != null || cfg.dinnerDescription.ar != null)) {
      details = String(this.resolveText(cfg.dinnerDescription) || '').trim();
    }
    if (!details) {
      details = [this.resolveText(dinner.dinnerDate), this.resolveText(dinner.dinnerTime)]
        .filter((x) => x && String(x).trim())
        .join('\n');
    }
    const resto = String(this.resolveText(dinner.dinnerResto) || '').trim();
    const loc = String(this.resolveText(dinner.dinnerLocation) || '').trim();
    const location = [resto, loc].filter(Boolean).join(', ');
    return { start, end, title, details, location };
  };

  downloadCalendarIcs = (kind) => {
    const w = kind === 'ceremony' ? this.buildCeremonyCalendarWindow() : this.buildDinnerCalendarWindow();
    if (!w) return;
    const uid = `${kind}-${this.state.suffix}-${w.start.getTime()}@wedding-card`;
    const stamp = formatGoogleCalendarUtc(new Date());
    const body = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//wedding-card//EN',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${formatGoogleCalendarUtc(w.start)}`,
      `DTEND:${formatGoogleCalendarUtc(w.end)}`,
      `SUMMARY:${escapeIcsText(w.title)}`,
      `DESCRIPTION:${escapeIcsText(w.details)}`,
      `LOCATION:${escapeIcsText(w.location)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = kind === 'ceremony' ? 'wedding-ceremony.ics' : 'wedding-reception.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  renderCalendarAddBlock = (kind) => {
    const cfg = this.getCalendarAddLive();
    if (!cfg) return null;
    const w = kind === 'ceremony' ? this.buildCeremonyCalendarWindow() : this.buildDinnerCalendarWindow();
    if (!w) return null;
    const labels = cfg.labels || {};
    const gl = labels.addGoogle || { en: 'Google Calendar', ar: 'تقويم جوجل', lang: 'en' };
    const il = labels.addIcs || { en: 'Apple / Outlook (.ics)', ar: 'ملف التقويم (.ics)', lang: 'en' };
    const badge =
      kind === 'ceremony'
        ? labels.ceremonyBadge || { en: 'Save the date', ar: 'احفظ التاريخ', lang: 'en' }
        : labels.dinnerBadge || { en: 'Reception', ar: 'الحفل', lang: 'en' };
    const googleUrl = buildGoogleCalendarUrl(w);
    const showGoogle = cfg.showGoogleCalendar !== false;
    const showIcs = cfg.showIcsDownload !== false;
    if (!showGoogle && !showIcs) return null;
    const badgeText = badge ? String(this.resolveText(badge) || '').trim() : '';

    return (
      <div className="calendar-add-block" role="group" aria-label={badgeText || undefined}>
        {badgeText ? (
          <div className="calendar-add-block__badge">{badgeText}</div>
        ) : null}
        <div className="calendar-add-block__actions">
          {showGoogle ? (
            <a
              className="calendar-add-block__btn"
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {this.resolveText(gl)}
            </a>
          ) : null}
          {showIcs ? (
            <button type="button" className="calendar-add-block__btn" onClick={() => this.downloadCalendarIcs(kind)}>
              {this.resolveText(il)}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  /** 3D “turning book page” motion while scrolling slides; off when reduced-motion or disabled in JSON. */
  getBookPageSwipeLive = () => {
    if (this.getScrollSinglePageLive()) return null;
    const ui = this.state.AppData && this.state.AppData.ui;
    const cfg = ui && ui.bookPageSwipe;
    if (!cfg || cfg.enabled !== true) return null;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return null;
    }
    return cfg;
  };

  resetBookPageFlipTransforms = () => {
    const strip = this.swipeStripRef.current;
    if (!strip) return;
    strip.querySelectorAll('.card-swipe-page').forEach((p) => {
      p.style.transform = '';
      p.style.transformOrigin = '';
      p.style.willChange = '';
      p.style.boxShadow = '';
      p.style.zIndex = '';
      p.style.filter = '';
      p.style.removeProperty('--book-page-edge-shade');
    });
  };

  updateBookPageFlip = () => {
    this._stripScrollFlipScheduled = false;
    const cfg = this.getBookPageSwipeLive();
    if (!cfg) {
      this.resetBookPageFlipTransforms();
      return;
    }
    const strip = this.swipeStripRef.current;
    if (!strip) return;
    const pages = strip.querySelectorAll('.card-swipe-page');
    if (!pages.length) return;
    const rect = strip.getBoundingClientRect();
    const w = rect.width;
    if (!w) return;

    let maxAngle = Number(cfg.maxAngleDeg);
    if (!Number.isFinite(maxAngle)) maxAngle = 36;
    maxAngle = Math.min(55, Math.max(6, maxAngle));

    let depthPx = Number(cfg.depthPx);
    if (!Number.isFinite(depthPx)) depthPx = 14;
    depthPx = Math.min(48, Math.max(0, depthPx));

    let curve = Number(cfg.curve);
    if (!Number.isFinite(curve)) curve = 1.35;
    curve = Math.min(2.2, Math.max(0.6, curve));

    let cornerLift = Number(cfg.cornerLiftDeg);
    if (!Number.isFinite(cornerLift)) cornerLift = 14;
    cornerLift = Math.min(30, Math.max(0, cornerLift));

    let hingeY = Number(cfg.hingeYPercent);
    if (!Number.isFinite(hingeY)) hingeY = 90;
    hingeY = Math.min(100, Math.max(50, hingeY));

    /* <1: rotation builds faster at the end of the drag (more “paper” resistance at first). */
    let easeExponent = Number(cfg.easeExponent);
    if (!Number.isFinite(easeExponent)) easeExponent = 0.86;
    easeExponent = Math.min(1, Math.max(0.55, easeExponent));

    /* Bell-shaped extra rotateX — strongest mid-turn, reads like the sheet lifting off the stack. */
    let curlExtraDeg = Number(cfg.curlExtraDeg);
    if (!Number.isFinite(curlExtraDeg)) curlExtraDeg = 12;
    curlExtraDeg = Math.min(28, Math.max(0, curlExtraDeg));

    let shadowStrength = Number(cfg.shadowStrength);
    if (!Number.isFinite(shadowStrength)) shadowStrength = 1;
    shadowStrength = Math.min(1.35, Math.max(0, shadowStrength));

    /* Horizontal foreshortening from rotateY (cosine of tilt). */
    let foreshortenMin = Number(cfg.foreshortenMin);
    if (!Number.isFinite(foreshortenMin)) foreshortenMin = 0.82;
    foreshortenMin = Math.min(0.98, Math.max(0.72, foreshortenMin));

    const containerMidX = rect.left + w / 2;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pr = page.getBoundingClientRect();
      const pageMidX = pr.left + pr.width / 2;
      const delta = (pageMidX - containerMidX) / w;
      const raw = Math.max(-1, Math.min(1, delta * curve));
      const turn = Math.sign(raw) * Math.pow(Math.abs(raw), easeExponent);
      const absT = Math.abs(turn);
      const ry = turn * -maxAngle;
      const z = -absT * depthPx;

      let originX = '50%';
      let rx = 0;
      if (turn < -0.02) {
        originX = '100%';
        rx = absT * cornerLift;
      } else if (turn > 0.02) {
        originX = '0%';
        rx = -absT * cornerLift;
      }

      const curlBell = Math.sin(Math.min(1, absT) * Math.PI);
      let hingeDir = 0;
      if (turn < -0.02) hingeDir = 1;
      else if (turn > 0.02) hingeDir = -1;
      const rxTotal = rx + hingeDir * curlBell * curlExtraDeg;

      const rad = Math.abs(ry) * (Math.PI / 180);
      const scaleX = Math.max(foreshortenMin, Math.cos(rad));

      const originY = `${hingeY}%`;
      page.style.willChange = 'transform, box-shadow';
      page.style.transformOrigin = `${originX} ${originY}`;
      page.style.transform = `translateZ(${z}px) rotateX(${rxTotal}deg) rotateY(${ry}deg) scaleX(${scaleX})`;

      /* Mid-turn page above neighbors so the “leaf” overlaps like a real stack. */
      page.style.zIndex = String(100 + Math.round(absT * 600));

      page.style.setProperty('--book-page-edge-shade', (absT * 0.55).toFixed(3));

      if (shadowStrength > 0 && absT > 0.04) {
        const blur = 10 + absT * 42;
        const spread = 2 + absT * 14;
        const alpha = (0.12 + absT * 0.38) * shadowStrength;
        const lift = 6 + absT * 18;
        const xOff = turn < 0 ? -spread - absT * 8 : spread + absT * 8;
        page.style.boxShadow = `${xOff}px ${lift}px ${blur}px rgba(0, 0, 0, ${alpha.toFixed(3)})`;
        page.style.filter = `brightness(${1 - absT * 0.06 * shadowStrength})`;
      } else {
        page.style.boxShadow = '';
        page.style.filter = '';
      }
    }
  };

  scheduleBookPageFlip = () => {
    if (this._stripScrollFlipScheduled) return;
    this._stripScrollFlipScheduled = true;
    requestAnimationFrame(() => this.updateBookPageFlip());
  };

  attachSwipeStripBookFlipListeners = () => {
    const el = this.swipeStripRef.current;
    if (!el || this._stripBookFlipScrollHandler) return;
    if (!this.getBookPageSwipeLive()) return;
    this._stripBookFlipScrollHandler = () => this.scheduleBookPageFlip();
    el.addEventListener('scroll', this._stripBookFlipScrollHandler, { passive: true });
    this._stripBookFlipResizeHandler = () => {
      if (this.getBookPageSwipeLive()) this.scheduleBookPageFlip();
    };
    window.addEventListener('resize', this._stripBookFlipResizeHandler, { passive: true });
  };

  detachSwipeStripBookFlipListeners = () => {
    const el = this.swipeStripRef.current;
    if (el && this._stripBookFlipScrollHandler) {
      el.removeEventListener('scroll', this._stripBookFlipScrollHandler);
    }
    this._stripBookFlipScrollHandler = null;
    if (this._stripBookFlipResizeHandler) {
      window.removeEventListener('resize', this._stripBookFlipResizeHandler);
      this._stripBookFlipResizeHandler = null;
    }
    this._stripScrollFlipScheduled = false;
    this.resetBookPageFlipTransforms();
  };

  isEnvelopeFeelEnabled = () => {
    const ef = this.state.AppData && this.state.AppData.ui && this.state.AppData.ui.envelopeFeel;
    return ef != null && ef.enabled === true;
  };

  isCurtainFeelEnabled = () => {
    if (this.isEnvelopeFeelEnabled()) return false;
    const cf = this.state.AppData && this.state.AppData.ui && this.state.AppData.ui.curtainFeel;
    return cf != null && cf.enabled === true;
  };

  getCountdownScratchLive = () => {
    const c = this.state.AppData && this.state.AppData.ui && this.state.AppData.ui.countdownScratch;
    if (!c || c.enabled !== true) return null;
    return c;
  };

  handleStartPageOpen = () => {
    if (this.state.didstart) return;
    const useEnvelope = this.isEnvelopeFeelEnabled();
    const useCurtain = this.isCurtainFeelEnabled();
    if (!useEnvelope && !useCurtain) {
      this.startCardTour(false);
      return;
    }
    if (this.state.startPageReveal) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this.startCardTour(true);
      return;
    }
    this.setState({ startPageReveal: true });
    if (this._startRevealTimer) clearTimeout(this._startRevealTimer);
    this._startRevealTimer = setTimeout(() => {
      this._startRevealTimer = null;
      this.startCardTour(true);
    }, 1150);
  };

  getLastPageFireworksSettings = () => {
    const lp = this.state.AppData && this.state.AppData.ui && this.state.AppData.ui.lastPage;
    const tf = lp && lp.tapFireworks;
    const isOff = (v) => v === false || v === 'false' || v === 0 || v === '0';
    let tapEnabled = true;
    let showHint = true;
    if (tf != null) {
      tapEnabled = tf.enabled === undefined || tf.enabled === null ? true : !isOff(tf.enabled);
      showHint = tf.showHint === undefined || tf.showHint === null ? true : !isOff(tf.showHint);
    }
    let brightness = 1;
    if (lp && typeof lp.fireworksBrightness === 'number' && !Number.isNaN(lp.fireworksBrightness)) {
      brightness = Math.min(2.5, Math.max(0.35, lp.fireworksBrightness));
    }
    let tapMax = 26;
    if (lp && typeof lp.fireworksTapMax === 'number' && lp.fireworksTapMax > 0) {
      tapMax = Math.min(40, Math.floor(lp.fireworksTapMax));
    }
    return { lp, tf, tapEnabled, showHint, brightness, tapMax };
  };

  handleLastPageTapFirework = (e) => {
    if (!this.state.displayLastPage) return;
    const { tapEnabled, tapMax } = this.getLastPageFireworksSettings();
    if (!tapEnabled) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const hue = Math.floor(Math.random() * 360);
    this.setState((prev) => ({
      lastPageUserFireworks: [...prev.lastPageUserFireworks, { id, x, y, hue }].slice(-tapMax),
      lastPageHintDismissed: true,
    }));
  };

  handleLastPageUserFireworkEnd = (id) => {
    this.setState((prev) => ({
      lastPageUserFireworks: prev.lastPageUserFireworks.filter((f) => f.id !== id),
    }));
  };


  componentDidMount = async () => {
    this.attachSwipeStripWheelListener();
    this.attachSwipeStripBookFlipListeners();
    this.startCountdown();
    this.normalizeLeafPositions();
    this.initLeaves();
    if (!this._leafResizeHandler) {
      this._leafResizeHandler = () => this.normalizeLeafPositions();
      window.addEventListener('resize', this._leafResizeHandler, { passive: true });
    }

    let tempData
    let guestID = window.location.search
    // let guestID = "?id=ghjy29xnla9d8aa"

    await axios({
      method: "get",
      url:`${this.state.apiCall}/search${guestID}&Suffix=${this.state.suffix}`,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(response => {
        tempData = response.data
      })
      .catch(error => {
        if (axios.isCancel(error)) { console.log(error.message); }
      });

    const listOfInv = tempData && tempData.ListOfInv ? JSON.parse(tempData.ListOfInv) : [];
    this.setState({
      guestID: tempData && tempData.id ? tempData.id : '',
      invitationTo: tempData && tempData.name ? tempData.name : '',
      nbrOfInvited: tempData && tempData.NumberOfGuests ? tempData.NumberOfGuests : '',
      guestPhoneNumber: tempData && tempData.mobilenumber ? tempData.mobilenumber : '',
      wtpsMsg: tempData && tempData.wtpsMsg ? tempData.wtpsMsg : '',
      listOfInv
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.AppData !== this.state.AppData ||
      prevState.didstart !== this.state.didstart ||
      prevState.swipeViewsOpaque !== this.state.swipeViewsOpaque
    ) {
      if (this.getBookPageSwipeLive()) {
        this.scheduleBookPageFlip();
      } else {
        this.resetBookPageFlipTransforms();
      }
    }
  }

  componentWillUnmount() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.detachSwipeStripWheelListener();
    this.detachSwipeStripBookFlipListeners();
    if (this._startRevealTimer) {
      clearTimeout(this._startRevealTimer);
      this._startRevealTimer = null;
    }
    if (this._leafResizeHandler) {
      window.removeEventListener('resize', this._leafResizeHandler);
      this._leafResizeHandler = null;
    }
  }

  /**
   * Convert the huge hard-coded `left: ####px` offsets into viewport units so
   * leaves stay on-screen on mobile browsers.
   */
  normalizeLeafPositions = () => {
    if (typeof document === 'undefined') return;
    const root = document.getElementById('leafContainer');
    if (!root) return;
    const vw = Math.max(1, window.innerWidth || 1);
    const children = root.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      if (!(el instanceof HTMLElement)) continue;
      if (el.dataset && el.dataset.leafGen === '1') continue;
      const raw = (el.style && el.style.left) || '';
      if (!raw || !raw.endsWith('px')) continue;
      const px = Number.parseFloat(raw);
      if (!Number.isFinite(px)) continue;

      // Original values were authored for a very wide canvas (thousands of px).
      // We remap them into a stable 0–96vw lane so every leaf remains visible.
      let v = (px % 1000) / 10; // 0..99.9
      if (!Number.isFinite(v)) v = (px / 5000) * 100;
      v = Math.max(0, Math.min(96, v));

      // Avoid clustering at the very edges.
      const gutter = Math.min(4, Math.max(1, (45 / vw) * 100));
      v = Math.max(gutter, Math.min(100 - gutter, v));
      el.style.left = `${v}vw`;
    }
  };

  initCountdownScratch = () => {
    const cfg = this.getCountdownScratchLive();
    if (!cfg) return;
    const cover = cfg.coverColor || 'rgba(255,255,255,0.92)';
    const grad = cfg.coverGradient;
    const text = cfg.coverText || { en: 'Scratch', ar: 'اكشط', lang: 'en' };
    const t = String(this.resolveText(text) || '').trim() || 'Scratch';
    const units = ['days', 'hours', 'minutes', 'seconds'];
    units.forEach((k) => {
      const c = this._scratchCanvases[k];
      if (!c) return;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const w = c.width;
      const h = c.height;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, w, h);
      if (grad && grad.from && grad.to) {
        const angle = Number(grad.angleDeg);
        const a = Number.isFinite(angle) ? angle : 90;
        const rad = (a * Math.PI) / 180;
        const x = Math.cos(rad);
        const y = Math.sin(rad);
        const x0 = w * (0.5 - 0.5 * x);
        const y0 = h * (0.5 - 0.5 * y);
        const x1 = w * (0.5 + 0.5 * x);
        const y1 = h * (0.5 + 0.5 * y);
        const g = ctx.createLinearGradient(x0, y0, x1, y1);
        g.addColorStop(0, String(grad.from));
        g.addColorStop(1, String(grad.to));
        ctx.fillStyle = g;
      } else {
        ctx.fillStyle = cover;
      }
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.font = `600 ${Math.max(12, Math.floor(w * 0.16))}px 'Lora', serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t, w / 2, h / 2);
    });
  };

  scheduleScratchCheck = (unitKey) => {
    if (this._scratchCheckScheduled) return;
    this._scratchCheckScheduled = true;
    requestAnimationFrame(() => {
      this._scratchCheckScheduled = false;
      this.checkScratchReveal(unitKey);
    });
  };

  checkScratchReveal = (unitKey) => {
    const cfg = this.getCountdownScratchLive();
    if (!cfg) return;
    const c = this._scratchCanvases[unitKey];
    if (!c) return;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const w = c.width;
    const h = c.height;
    if (!w || !h) return;

    // Sample a small grid to estimate erased coverage (alpha == 0-ish).
    const img = ctx.getImageData(0, 0, w, h).data;
    const step = Math.max(6, Math.floor(w / 14));
    let total = 0;
    let cleared = 0;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        total++;
        const a = img[(y * w + x) * 4 + 3];
        if (a < 18) cleared++;
      }
    }
    const ratio = total ? cleared / total : 0;
    let threshold = Number(cfg.revealThreshold);
    if (!Number.isFinite(threshold)) threshold = 0.42;
    threshold = Math.min(0.9, Math.max(0.15, threshold));
    if (ratio >= threshold) {
      this.setState(
        (prev) => ({
          scratchRevealed: { ...prev.scratchRevealed, [unitKey]: true },
        }),
        () => {
          const cc = this._scratchCanvases[unitKey];
          if (!cc) return;
          const cctx = cc.getContext('2d');
          if (!cctx) return;
          cctx.clearRect(0, 0, cc.width, cc.height);
        }
      );
    }
  };

  scratchPointerDown = (unitKey, e) => {
    const cfg = this.getCountdownScratchLive();
    if (!cfg) return;
    if (this.state.scratchRevealed[unitKey]) return;
    const c = this._scratchCanvases[unitKey];
    if (!c) return;
    e.preventDefault();
    const rect = c.getBoundingClientRect();
    this._scratchPointer = { unitKey, id: e.pointerId, rect };
    try {
      c.setPointerCapture(e.pointerId);
    } catch (_) {}
    this.scratchPointerMove(unitKey, e);
  };

  scratchPointerMove = (unitKey, e) => {
    const cfg = this.getCountdownScratchLive();
    if (!cfg) return;
    if (this.state.scratchRevealed[unitKey]) return;
    const c = this._scratchCanvases[unitKey];
    if (!c) return;
    if (!this._scratchPointer || this._scratchPointer.id !== e.pointerId) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const rect = this._scratchPointer.rect || c.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * c.width;
    const y = ((e.clientY - rect.top) / rect.height) * c.height;
    let r = Number(cfg.brushRadiusPx);
    if (!Number.isFinite(r)) r = 14;
    r = Math.min(34, Math.max(8, r));
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    this.scheduleScratchCheck(unitKey);
  };

  scratchPointerUp = (unitKey, e) => {
    const c = this._scratchCanvases[unitKey];
    if (c) {
      try {
        c.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
    if (this._scratchPointer && this._scratchPointer.id === e.pointerId) {
      this._scratchPointer = null;
    }
  };

  renderCountdownValueScratch = (unitKey) => {
    const cfg = this.getCountdownScratchLive();
    if (!cfg) return null;
    const revealed = this.state.scratchRevealed[unitKey] === true;
    const size = 58;
    return (
      !revealed ? (
        <canvas
          className="countdown-scratch-overlay"
          width={size}
          height={size}
          ref={(el) => {
            this._scratchCanvases[unitKey] = el;
            if (el) this.initCountdownScratch();
          }}
          onPointerDown={(e) => this.scratchPointerDown(unitKey, e)}
          onPointerMove={(e) => this.scratchPointerMove(unitKey, e)}
          onPointerUp={(e) => this.scratchPointerUp(unitKey, e)}
          onPointerCancel={(e) => this.scratchPointerUp(unitKey, e)}
        />
      ) : null
    );
  };

  attachSwipeStripWheelListener = () => {
    const el = this.swipeStripRef.current;
    if (!el || this._stripWheelHandler) return;
    if (this.getScrollSinglePageLive()) return;
    this._stripWheelHandler = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && e.deltaX !== 0) {
        el.scrollLeft += e.deltaX;
        e.preventDefault();
      } else if (e.shiftKey && e.deltaY !== 0) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', this._stripWheelHandler, { passive: false });
  };

  detachSwipeStripWheelListener = () => {
    const el = this.swipeStripRef.current;
    if (el && this._stripWheelHandler) {
      el.removeEventListener('wheel', this._stripWheelHandler);
    }
    this._stripWheelHandler = null;
  };

  snapSwipeStrip = () => {
    if (this.getScrollSinglePageLive()) return;
    const el = this.swipeStripRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (!w) return;
    const page = Math.round(el.scrollLeft / w);
    const totalPages = Math.max(1, Math.round(el.scrollWidth / w));
    const maxPage = totalPages - 1;
    const clamped = Math.min(Math.max(0, page), maxPage);
    el.scrollTo({ left: clamped * w, behavior: 'smooth' });
  };

  /** Desktop / pen: drag horizontally; touch uses native scrolling. */
  handleStripPointerDown = (e) => {
    if (this.getScrollSinglePageLive()) return;
    if (e.pointerType === 'touch') return;
    if (e.button !== 0) return;
    const t = e.target;
    if (t && typeof t.closest === 'function') {
      if (t.closest('a, button, input, textarea, select, label')) return;
      if (t.closest('#muteUnmute')) return;
    }
    const el = this.swipeStripRef.current;
    if (!el) return;
    e.preventDefault();
    this.stripPointerDrag = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
  };

  handleStripPointerMove = (e) => {
    const d = this.stripPointerDrag;
    if (!d || d.pointerId !== e.pointerId) return;
    const el = this.swipeStripRef.current;
    if (!el) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 2) d.moved = true;
    el.scrollLeft = d.startScroll - dx;
    if (this.getBookPageSwipeLive()) this.scheduleBookPageFlip();
    e.preventDefault();
  };

  handleStripPointerUp = (e) => {
    const d = this.stripPointerDrag;
    if (!d || d.pointerId !== e.pointerId) return;
    const el = this.swipeStripRef.current;
    if (el) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      if (d.moved) this.snapSwipeStrip();
    }
    this.stripPointerDrag = null;
  };

  handleStripLostPointerCapture = (e) => {
    const d = this.stripPointerDrag;
    if (!d || d.pointerId !== e.pointerId) return;
    if (d.moved) this.snapSwipeStrip();
    this.stripPointerDrag = null;
  };

  getDefaultLang = () => {
    const s = this.state.AppData && this.state.AppData.settings;
    if (s && (s.defaultLang === 'ar' || s.defaultLang === 'en')) return s.defaultLang;
    if (s && s.textDisplay === 'ar') return 'ar';
    if (s && s.textDisplay === 'en') return 'en';
    if (s && s.primaryLang === 'ar') return 'ar';
    return 'en';
  };

  /** @returns {'en'|'ar'} */
  normalizeLang = (v) => (v === 'ar' ? 'ar' : 'en');

  /** Read `obj.en` or `obj.ar` only (no `lang` key on object). */
  pickLocalized = (obj, lang) => {
    if (obj == null) return '';
    if (typeof obj === 'string') return obj;
    const l = lang === 'ar' ? 'ar' : 'en';
    const primary = obj[l];
    const fallback = obj[l === 'ar' ? 'en' : 'ar'];
    if (primary != null && String(primary).trim() !== '') return String(primary);
    return fallback != null ? String(fallback) : '';
  };

  /** Render a simple `{ en, ar }` object using an explicit language. */
  renderLocalizedField = (obj, style = {}, reactKey = 'loc', lang) => {
    const l = lang === 'ar' || lang === 'en' ? lang : this.getDefaultLang();
    const text = this.pickLocalized(obj, l);
    if (!text) return null;
    return (
      <div key={reactKey} style={style} dir={l === 'ar' ? 'rtl' : 'ltr'}>{text}</div>
    );
  };

  resolveLang = (bi, forcedLang) => {
    if (typeof bi === 'string' || bi == null) return this.getDefaultLang();
    if (forcedLang === 'ar' || forcedLang === 'en') return forcedLang;
    if (bi.lang === 'ar' || bi.lang === 'en') return bi.lang;
    if (bi.display === 'ar' || bi.display === 'en') return this.normalizeLang(bi.display);
    return this.getDefaultLang();
  };

  resolveText = (bi, forcedLang) => {
    if (bi == null) return '';
    if (typeof bi === 'string') return bi;
    const lang = this.resolveLang(bi, forcedLang);
    const primary = lang === 'ar' ? bi.ar : bi.en;
    const fallback = lang === 'ar' ? bi.en : bi.ar;
    if (primary != null && String(primary).trim() !== '') return primary;
    return fallback || '';
  };

  applyDateTemplate = (text, lang) => {
    if (text == null || !String(text).includes('{date}')) return text;
    const short = this.state.AppData.ceremonyDateShort || {};
    const d = lang === 'ar' ? (short.ar || short.en || '') : (short.en || short.ar || '');
    return String(text).replace(/\{date\}/g, d);
  };

  /**
   * Renders a bilingual field from JSON (`en` / `ar` + optional `lang`).
   * @param {object|string} field — e.g. ui.welcome, slide1.gettingMarried
   * @param {object} style — CSS for the wrapper div
   * @param {'en'|'ar'} [forcedLang] — optional override (rare)
   */
  renderText = (field, style = {}, forcedLang) => {
    const lang = this.resolveLang(field, forcedLang);
    let text = this.resolveText(field, forcedLang);
    text = this.applyDateTemplate(text, lang);
    if (!text) return null;
    return (
      <div style={style} dir={lang === 'ar' ? 'rtl' : 'ltr'}>{text}</div>
    );
  };

  /** Which script to use for names on each slide (from `display.slide1` / `display.slide2` in AppStaticData). */
  getNameLanguagesForSlide = (slideId) => {
    const row = (this.state.AppData.display && this.state.AppData.display[slideId]) || {};
    return {
      husband: this.normalizeLang(row.husband),
      bride: this.normalizeLang(row.bride),
      ampersand: this.normalizeLang(row.ampersand),
    };
  };

  /** marriageQuotes.lang drives which token matches the visible quote block */
  getMarriageQuotesDisplayLang = () => {
    const mq = this.state.AppData && this.state.AppData.marriageQuotes;
    if (mq && (mq.lang === 'ar' || mq.lang === 'en')) return mq.lang;
    return this.getDefaultLang();
  };

  slide2CoupleTextStyle = (lang) => {
    const mqLang = this.getMarriageQuotesDisplayLang();
    const nameSize = mqLang === 'ar' ? SLIDE2_MQ_FONT.rtlName : SLIDE2_MQ_FONT.ltrName;
    const fontSize =
      mqLang === 'ar' && lang === 'en'
        ? `calc(${nameSize} * ${SLIDE2_EN_COUPLE_WITH_AR_MQ_SCALE})`
        : nameSize;
    if (lang === 'ar') {
      return {
        fontFamily: "'Tajawal', sans-serif",
        fontSize,
        fontWeight: 600,
        lineHeight: 1.68,
        letterSpacing: '0.025em',
      };
    }
    return {
      fontFamily: "'Great Vibes', cursive",
      fontSize,
      fontWeight: 600,
      fontStyle: 'italic',
      lineHeight: 1.35,
    };
  };

  /** Same size tier as marriageQuotes parent-name lines (lines 1 & 3). */
  slide2AmpersandTextStyle = (lang) => {
    const mqLang = this.getMarriageQuotesDisplayLang();
    const size = mqLang === 'ar' ? SLIDE2_MQ_FONT.rtlName : SLIDE2_MQ_FONT.ltrName;
    const fontSize =
      mqLang === 'ar' && lang === 'en'
        ? `calc(${size} * ${SLIDE2_EN_COUPLE_WITH_AR_MQ_SCALE})`
        : size;
    if (lang === 'ar') {
      return {
        fontFamily: "'Tajawal', sans-serif",
        fontSize,
        fontWeight: 600,
        lineHeight: 1.68,
        paddingTop: '2px',
      };
    }
    return {
      fontFamily: "'Great Vibes', cursive",
      fontSize,
      fontWeight: 600,
      fontStyle: 'italic',
      lineHeight: 1.35,
      paddingTop: '2px',
    };
  };

  slide2EvangelistTextStyle = (lang) => {
    const l = lang === 'ar' ? 'ar' : 'en';
    if (l === 'ar') {
      return {
        fontFamily: "'El Messiri', 'Tajawal', sans-serif",
        fontSize: SLIDE2_EVANGELIST_FONT.rtl,
        fontWeight: 400,
        lineHeight: 1.55,
        letterSpacing: '0.01em',
      };
    }
    return {
      fontFamily: "'Cormorant Garamond', 'Lora', serif",
      fontSize: SLIDE2_EVANGELIST_FONT.ltr,
      fontStyle: 'italic',
      fontWeight: 500,
      lineHeight: 1.4,
      opacity: 0.95,
    };
  };

  /**
   * @param {'husband'|'bride'} role
   * @param {'en'|'ar'} lang — `AppData.husband[lang]` or `AppData.bride[lang]`
   */
  renderPersonName = (role, style, lang) => {
    const person = role === 'husband' ? this.state.AppData.husband : this.state.AppData.bride;
    return this.renderLocalizedField(person, style, role, lang);
  };

  getUiLine = (bi) => this.resolveText(bi);

  pickLangString = (bi, forcedLang) => this.resolveText(bi, forcedLang);

  getCountdownUnitLabel = (unitKey) => {
    const u = this.state.AppData.ui && this.state.AppData.ui.countdownUnits && this.state.AppData.ui.countdownUnits[unitKey];
    if (!u) return unitKey;
    return this.resolveText(u);
  };

  getStartLinesLang = () => {
    const sl = this.state.AppData.ui && this.state.AppData.ui.startLines;
    if (sl && (sl.lang === 'ar' || sl.lang === 'en')) return sl.lang;
    return this.getDefaultLang();
  };

  getStartLines = () => {
    const lines = this.state.AppData.ui && this.state.AppData.ui.startLines;
    const lang = this.getStartLinesLang();
    if (!lines) return ['Tap', 'to', 'Start'];
    const arr = (lang === 'ar' ? lines.ar : lines.en) || lines.en || lines.ar || [];
    return arr;
  };

  renderMarriageQuotes = (lineClassName = '') => {
    const mq = this.state.AppData.marriageQuotes;
    if (!mq) return null;
    const renderList = (arr) => (arr || []).map((eachElem, key) => {
      if (eachElem === '') return <br key={key} />;
      return (
        <div key={key} className={lineClassName || undefined}>
          {eachElem}
        </div>
      );
    });
    if (mq.en && mq.ar) {
      const lang = mq.lang === 'ar' || mq.lang === 'en' ? mq.lang : this.getDefaultLang();
      return renderList(lang === 'ar' ? mq.ar : mq.en);
    }
    if (Array.isArray(mq)) return renderList(mq);
    return null;
  };

  renderSlide2CeremonyBlock = () => {
    const AppData = this.state.AppData;
    const lead = AppData.slide2CeremonyLead;
    const sub = AppData.slide2CeremonySubline;
    const mqLang = this.getMarriageQuotesDisplayLang();
    const leadSize = mqLang === 'ar' ? SLIDE2_MQ_FONT.rtlBody : SLIDE2_MQ_FONT.ltrBody;
    /* Subline: same tier as main marriageQuotes lines (not the smaller last-line size). */
    const subSize = mqLang === 'ar' ? SLIDE2_MQ_FONT.rtlBody : SLIDE2_MQ_FONT.ltrName;
    const nodes = [];
    if (lead && biHasContent(lead)) {
      const lang = this.resolveLang(lead);
      let text = this.resolveText(lead);
      text = this.applyDateTemplate(text, lang);
      if (text) {
        const leadStyle = {
          fontSize: slide2FontWithArQuotesOptical(mqLang, lang, leadSize),
          lineHeight: 1.68,
          fontWeight: 500,
          letterSpacing: lang === 'ar' ? '0.02em' : '0.02em',
          fontFamily: lang === 'ar' ? "'El Messiri', 'Tajawal', sans-serif" : "'Cormorant Garamond', 'Lora', serif",
        };
        nodes.push(
          <div key="s2-lead" className="slide2-ceremony-line" dir={lang === 'ar' ? 'rtl' : 'ltr'} style={leadStyle}>
            {text}
          </div>
        );
      }
    }
    if (sub && biHasContent(sub)) {
      const lang = this.resolveLang(sub);
      let text = this.resolveText(sub);
      text = this.applyDateTemplate(text, lang);
      if (text) {
        const subStyle = {
          fontSize: slide2FontWithArQuotesOptical(mqLang, lang, subSize),
          lineHeight: mqLang === 'ar' ? 1.68 : 1.42,
          fontWeight: mqLang === 'ar' ? 500 : 600,
          letterSpacing: lang === 'ar' ? '0.02em' : '0.02em',
          fontFamily: lang === 'ar' ? "'El Messiri', 'Tajawal', sans-serif" : "'Cormorant Garamond', 'Lora', serif",
          fontStyle: lang === 'ar' ? 'normal' : 'italic',
          padding: '6px 12px 0',
        };
        nodes.push(
          <div key="s2-sub" className="slide2-ceremony-sub" dir={lang === 'ar' ? 'rtl' : 'ltr'} style={subStyle}>
            {text}
          </div>
        );
      }
    }
    return nodes.length ? <>{nodes}</> : null;
  };

  startCountdown = () => {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    const settings = (this.state.AppData && this.state.AppData.settings) || {};
    const iso = settings.countdownTargetIso || '2026-06-20T19:30:00+03:00';
    const tz = settings.timeZone || 'Asia/Beirut';
    const weddingDate = new Date(iso).getTime();

    this.countdownTimer = setInterval(() => {
      const now = new Date().toLocaleString('en-US', { timeZone: tz });
      const currentTime = new Date(now).getTime();
      const distance = weddingDate - currentTime;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (distance < 0) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.setState({
          countdown: { days: 0, hours: 0, minutes: 0, seconds: 0 },
        });
      } else {
        this.setState({
          countdown: { days, hours, minutes, seconds },
        });
      }
    }, 1000);
  };

  isMusicEnabled = () => {
    const m = this.state.AppData.backgroundMusic;
    return Boolean(m && m.enabled && m.src);
  };

  mediaUrl = (url) => cacheBustMediaUrl(url, this.state.AppData && this.state.AppData.assetsVersion);

  getStartPageMedia = () => {
    const custom = this.state.AppData.startPageMedia;
    const fallback = { type: 'image', src: '/img/HomePage.jpg' };
    if (!custom || !custom.src) return fallback;
    const type = (custom.type || 'image').toLowerCase() === 'video' ? 'video' : 'image';
    return { type, src: custom.src };
  };

  getSlideMedia = (slideKey) => {
    const defaults = DEFAULT_SLIDE_MEDIA[slideKey] || { type: 'image', src: '' };
    const custom = this.state.AppData.slideMedia && this.state.AppData.slideMedia[slideKey];
    if (!custom || !custom.src) return defaults;
    const type = (custom.type || 'image').toLowerCase() === 'video' ? 'video' : 'image';
    return { type, src: custom.src };
  };

  renderSlideBackground = (slideKey) => {
    const { type, src } = this.getSlideMedia(slideKey);
    if (!src) return null;
    const srcB = this.mediaUrl(src);
    if (type === 'video') {
      return (
        <div className="slide-background-layer" aria-hidden="true">
          <video className="slide-bg-video" muted playsInline loop autoPlay src={srcB} />
        </div>
      );
    }
    return (
      <div
        className="slide-background-layer slide-bg-image"
        style={{ backgroundImage: `url(${srcB})` }}
        aria-hidden="true"
      />
    );
  };

  syncAudioMute = (muted) => {
    if (this.musicRef.current && this.isMusicEnabled()) {
      this.musicRef.current.muted = muted;
    }
  };

  playBackgroundMusic = () => {
    if (!this.isMusicEnabled() || !this.musicRef.current) return;
    this.musicRef.current.muted = this.state.isVideoMuted;
    const p = this.musicRef.current.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  handleRsvpStatusChange = (index, status) => {
    this.setState(prevState => ({
      listOfInv: prevState.listOfInv.map((person, i) => {
        if (i === index) {
          return {
            ...person,
            status: person.status === status ? 'P' : status,
          };
        }
        return person;
      }),
    }));
  };

  startCardTour = (fromEnvelope = false) => {
    if (this._startRevealTimer) {
      clearTimeout(this._startRevealTimer);
      this._startRevealTimer = null;
    }
    const sv = document.getElementById('SwipeableViews');
    const sp = document.getElementById('startPage');
    if (sv) sv.style.visibility = 'visible';
    if (sp) sp.style.visibility = 'hidden';

    const useFade = fromEnvelope && this.isEnvelopeFeelEnabled();
    this.setState(
      {
        didstart: true,
        isVideoMuted: false,
        startPageReveal: false,
        swipeViewsOpaque: !useFade,
      },
      () => {
        this.syncAudioMute(false);
        this.playBackgroundMusic();
        const resetStripScroll = () => {
          const strip = this.swipeStripRef.current;
          if (!strip) return;
          if (this.getScrollSinglePageLive()) strip.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          else strip.scrollTo({ left: 0, behavior: 'auto' });
        };
        resetStripScroll();
        if (useFade) {
          requestAnimationFrame(() => {
            resetStripScroll();
            requestAnimationFrame(() => {
              this.setState({ swipeViewsOpaque: true });
            });
          });
        }
      }
    );
  }

  handleMute = () => {
    this.setState(
      (prev) => ({ isVideoMuted: !prev.isVideoMuted }),
      () => this.syncAudioMute(this.state.isVideoMuted)
    );
  }

  reserve = async () => {
    const numberOfAttendees = this.state.listOfInv.filter(p => p.status === 'A').length;

    let params = {
      id: this.state.guestID,
      Suffix: this.state.suffix,
      Attending: numberOfAttendees > 0 ? "yes" : "no",
      NumberOfAttendes: numberOfAttendees,
      ListOfInv: JSON.stringify(this.state.listOfInv)
    }
    await axios({
      method: "patch",
      url: `${this.state.apiCall}/RSV`,
      data: params,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(response => {
        if (response) {
          this.setState({
            displayLastPage: true,
            lastPageUserFireworks: [],
            lastPageHintDismissed: false,
          });
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) { console.log(error.message); }
      });

  }

  render() {
    let { AppData, isVideoMuted, didstart, startPageReveal, swipeViewsOpaque } = this.state
    const startMedia = this.getStartPageMedia();
    const startMediaResolved = {
      ...startMedia,
      src: startMedia.src ? this.mediaUrl(startMedia.src) : startMedia.src,
    };
    const gv = AppData.globalBackgroundVideo;
    const showGlobalVideo = didstart && gv && gv.enabled && gv.src;
    const globalVideoMuted = this.isMusicEnabled() || isVideoMuted;
    const ui = AppData.ui || {};
    const ceremony = AppData.ceremony || {};
    const amp = ui.ampersand || { en: '&', ar: '&' };
    const slide1Copy = ui.slide1 || {};
    const langSlide1 = this.getNameLanguagesForSlide('slide1');
    const langSlide2 = this.getNameLanguagesForSlide('slide2');
    const slide2Typo = AppData.slide2Typography || {};
    const slide2FillPage = slide2Typo.fillPage !== false;
    const slide2Compact = slide2Typo.compact === true;
    const mq = AppData.marriageQuotes;
    const mqDisplayLang =
      mq && (mq.lang === 'ar' || mq.lang === 'en') ? mq.lang : this.getDefaultLang();

    const useEnvelopeFeel = this.isEnvelopeFeelEnabled();
    const useCurtainFeel = this.isCurtainFeelEnabled();
    const scrollSinglePageLive = this.getScrollSinglePageLive();
    const bookPageSwipeLive = this.getBookPageSwipeLive();
    const bookPerspectivePx = bookPageSwipeLive
      ? (() => {
          const p = Number(bookPageSwipeLive.perspectivePx);
          return Number.isFinite(p) ? Math.min(2400, Math.max(500, p)) : 1400;
        })()
      : null;
    const startLinesLang = this.getStartLinesLang();
    const startLinesFiltered = this.getStartLines().filter(
      (line) => line != null && String(line).trim() !== ''
    );
    const envelopeFeelCfg = ui.envelopeFeel || {};
    const envelopeFeelTheme = useEnvelopeFeel ? buildEnvelopeFeelTheme(envelopeFeelCfg) : null;
    const curtainFeelCfg = ui.curtainFeel || {};
    const curtainFeelTheme = useCurtainFeel ? buildCurtainFeelTheme(curtainFeelCfg) : null;
    const sealLogoSrcRaw =
      (envelopeFeelCfg.sealLogoSrc && String(envelopeFeelCfg.sealLogoSrc).trim()) ||
      (ui.lastPage && ui.lastPage.logoSrc) ||
      '/img/Logo.jpeg';
    const sealLogoSrc = this.mediaUrl(sealLogoSrcRaw);

    const lastFw = this.getLastPageFireworksSettings();
    const tapHintBi = lastFw.tf && lastFw.tf.hint;
    let tapHintText = '';
    if (lastFw.tf == null) {
      tapHintText = 'Tap anywhere for your own fireworks ✨';
    } else if (tapHintBi) {
      tapHintText = String(this.resolveText(tapHintBi) || '').trim();
      if (!tapHintText) {
        tapHintText = String(tapHintBi.en || tapHintBi.ar || '').trim();
      }
    }
    if (lastFw.tf != null && lastFw.showHint && lastFw.tapEnabled && tapHintBi == null) {
      tapHintText = 'Tap anywhere for your own fireworks ✨';
    }
    if (!tapHintText) {
      tapHintText = 'Tap anywhere for your own fireworks ✨';
    }
    const showLastPageTapHint =
      this.state.displayLastPage === true &&
      lastFw.tapEnabled &&
      lastFw.showHint &&
      !this.state.lastPageHintDismissed;
    const lastPageFireworksBrightness = lastFw.brightness;

    return (
      <>
        <div id="swipeableViewsParentContainer" style={this.state.displayLastPage === true ? { display: "none" } : { display: "block" }}>
          {this.isMusicEnabled() && (
            <audio
              ref={this.musicRef}
              src={this.mediaUrl(AppData.backgroundMusic.src)}
              loop
              preload="auto"
              style={{ display: 'none' }}
            />
          )}
          <div id="leafContainer" data-image="bigLeaf" className="bigLeaf">
            {(this.getLeavesLive() ? this.state.leafInstances : []).map((l) => (
              <div
                key={l.id}
                data-leaf-gen="1"
                style={{
                  top: '115%',
                  left: `${l.leftVw}vw`,
                  animationName: 'fade, drop',
                  animationDuration: `${l.fallDur}s, ${l.fallDur}s`,
                  animationDelay: `${l.fallDelay}s, ${l.fallDelay}s`,
                }}
              >
                <img
                  alt="realLeaf"
                  src={l.img}
                  style={{ animationName: l.spinName, animationDuration: `${l.spinDur}s` }}
                />
              </div>
            ))}
            <div style={{ top: "115%", left: "4271px", animationName: "fade, drop", animationDuration: "21.5328s, 21.5328s", animationDelay: "0.649181s, 0.649181s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.2989s" }} />
            </div>
            <div style={{ top: "115%", left: "3209px", animationName: "fade, drop", animationDuration: "20.5643s, 20.5643s", animationDelay: "0.0979504s, 0.0979504s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "14.8381s" }} />
            </div>
            <div style={{ top: "115%", left: "1033px", animationName: "fade, drop", animationDuration: "32.6238s, 32.6238s", animationDelay: "0.761477s, 0.761477s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "11.7593s" }} />
            </div>
            <div style={{ top: "115%", left: "4494px", animationName: "fade, drop", animationDuration: "24.3477s, 24.3477s", animationDelay: "0.44706s, 0.44706s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.0681s" }} />
            </div>
            <div style={{ top: "115%", left: "2248px", animationName: "fade, drop", animationDuration: "26.1889s, 26.1889s", animationDelay: "0.078033s, 0.078033s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "11.4703s" }} />
            </div>
            <div style={{ top: "115%", left: "3547px", animationName: "fade, drop", animationDuration: "31.2799s, 31.2799s", animationDelay: "0.423443s, 0.423443s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "12.0255s" }} />
            </div>
            <div style={{ top: "115%", left: "205px", animationName: "fade, drop", animationDuration: "30.8377s, 30.8377s", animationDelay: "0.623964s, 0.623964s" }}>
              <img alt="realLeaf" src="/img/realLeaf3.png" style={{ animationName: "clockwiseSpin", animationDuration: "13.0019s" }} />
            </div>
            <div style={{ top: "115%", left: "3209px", animationName: "fade, drop", animationDuration: "20.5643s, 20.5643s", animationDelay: "0.0979504s, 0.0979504s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "14.8381s" }} />
            </div>
            <div style={{ top: "115%", left: "1033px", animationName: "fade, drop", animationDuration: "32.6238s, 32.6238s", animationDelay: "0.761477s, 0.761477s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "11.7593s" }} />
            </div>
            <div style={{ top: "115%", left: "4494px", animationName: "fade, drop", animationDuration: "24.3477s, 24.3477s", animationDelay: "0.44706s, 0.44706s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.0681s" }} />
            </div>
            <div style={{ top: "115%", left: "43px", animationName: "fade, drop", animationDuration: "35.0827s, 35.0827s", animationDelay: "0.36684s, 0.36684s" }}>
              <img alt="realLeaf" src="/img/realLeaf3.png" style={{ animationName: "clockwiseSpin", animationDuration: "15.6451s" }} />
            </div>
            <div style={{ top: "115%", left: "3209px", animationName: "fade, drop", animationDuration: "20.5643s, 20.5643s", animationDelay: "0.0979504s, 0.0979504s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "14.8381s" }} />
            </div>
            <div style={{ top: "115%", left: "1033px", animationName: "fade, drop", animationDuration: "32.6238s, 32.6238s", animationDelay: "0.761477s, 0.761477s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "11.7593s" }} />
            </div>
            <div style={{ top: "115%", left: "4494px", animationName: "fade, drop", animationDuration: "24.3477s, 24.3477s", animationDelay: "0.44706s, 0.44706s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.0681s" }} />
            </div>
            <div style={{ top: "115%", left: "2826px", animationName: "fade, drop", animationDuration: "35.7176s, 35.7176s", animationDelay: "0.142368s, 0.142368s" }}>
              <img alt="realLeaf" src="/img/realLeaf1.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.8249s" }} />
            </div>
            <div style={{ top: "115%", left: "621px", animationName: "fade, drop", animationDuration: "37.7397s, 37.7397s", animationDelay: "0.396265s, 0.396265s" }}>
              <img alt="realLeaf" src="/img/realLeaf3.png" style={{ animationName: "clockwiseSpin", animationDuration: "11.9869s" }} />
            </div>
            <div style={{ top: "115%", left: "295px", animationName: "fade, drop", animationDuration: "23.2892s, 23.2892s", animationDelay: "0.244458s, 0.244458s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "18.1451s" }} />
            </div>
            <div style={{ top: "115%", left: "3295px", animationName: "fade, drop", animationDuration: "21.5757s, 21.5757s", animationDelay: "0.833078s, 0.833078s" }}>
              <img alt="realLeaf" src="/img/realLeaf1.png" style={{ animationName: "clockwiseSpin", animationDuration: "15.8504s" }} />
            </div>
            <div style={{ top: "115%", left: "4014px", animationName: "fade, drop", animationDuration: "34.9891s, 34.9891s", animationDelay: "0.333657s, 0.333657s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "16.1805s" }} />
            </div>
            <div style={{ top: "115%", left: "84px", animationName: "fade, drop", animationDuration: "29.3781s, 29.3781s", animationDelay: "0.30615s, 0.30615s" }}>
              <img alt="realLeaf" src="/img/realLeaf1.png" style={{ animationName: "clockwiseSpin", animationDuration: "11.7186s" }} />
            </div>
            <div style={{ top: "115%", left: "3209px", animationName: "fade, drop", animationDuration: "20.5643s, 20.5643s", animationDelay: "0.0979504s, 0.0979504s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "14.8381s" }} />
            </div>
            <div style={{ top: "115%", left: "1033px", animationName: "fade, drop", animationDuration: "32.6238s, 32.6238s", animationDelay: "0.761477s, 0.761477s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "11.7593s" }} />
            </div>
            <div style={{ top: "115%", left: "4494px", animationName: "fade, drop", animationDuration: "24.3477s, 24.3477s", animationDelay: "0.44706s, 0.44706s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.0681s" }} />
            </div>
            <div style={{ top: "115%", left: "863px", animationName: "fade, drop", animationDuration: "23.7322s, 23.7322s", animationDelay: "0.76869s, 0.76869s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "14.3942s" }} />
            </div>
            <div style={{ top: "115%", left: "2771px", animationName: "fade, drop", animationDuration: "21.7417s, 21.7417s", animationDelay: "0.0370709s, 0.0370709s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "15.8919s" }} />
            </div>
            <div style={{ top: "115%", left: "935px", animationName: "fade, drop", animationDuration: "38.3105s, 38.3105s", animationDelay: "0.920299s, 0.920299s" }}>
              <img alt="realLeaf" src="/img/realLeaf1.png" style={{ animationName: "clockwiseSpin", animationDuration: "19.0806s" }} />
            </div>
            <div style={{ top: "115%", left: "3347px", animationName: "fade, drop", animationDuration: "37.37s, 37.37s", animationDelay: "0.0346564s, 0.0346564s" }}>
              <img alt="realLeaf" src="/img/realLeaf3.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.9137s" }} />
            </div>
            <div style={{ top: "115%", left: "3209px", animationName: "fade, drop", animationDuration: "20.5643s, 20.5643s", animationDelay: "0.0979504s, 0.0979504s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "14.8381s" }} />
            </div>
            <div style={{ top: "115%", left: "1033px", animationName: "fade, drop", animationDuration: "32.6238s, 32.6238s", animationDelay: "0.761477s, 0.761477s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "counterclockwiseSpinAndFlip", animationDuration: "11.7593s" }} />
            </div>
            <div style={{ top: "115%", left: "4494px", animationName: "fade, drop", animationDuration: "24.3477s, 24.3477s", animationDelay: "0.44706s, 0.44706s" }}>
              <img alt="realLeaf" src="/img/realLeaf2.png" style={{ animationName: "clockwiseSpin", animationDuration: "17.0681s" }} />
            </div>
            <div style={{ top: "115%", left: "1883px", animationName: "fade, drop", animationDuration: "22.9141s, 22.9141s", animationDelay: "0.144015s, 0.144015s" }}>
              <img alt="realLeaf" src="/img/realLeaf4.png" style={{ animationName: "clockwiseSpin", animationDuration: "11.7241s" }} />
            </div>
            <div style={{ top: "115%", left: "1440px", animationName: "fade, drop", animationDuration: "30.387s, 30.387s", animationDelay: "0.112946s, 0.112946s" }}>
              <img alt="realLeaf" src="/img/realLeaf1.png" style={{ animationName: "clockwiseSpin", animationDuration: "12.6348s" }} />
            </div>
          </div>

          {showGlobalVideo && (
            <video
              autoPlay
              muted={globalVideoMuted}
              playsInline
              loop
              controls={false}
              id="preweddingvideo"
            >
              <source src={this.mediaUrl(gv.src)} type="video/mp4" />
            </video>
          )}

          <div
            id="startPage"
            className={`swipeablePage${useEnvelopeFeel ? ' start-page--envelope-feel' : ''}${
              useCurtainFeel ? ' start-page--curtain-feel' : ''
            }${startPageReveal ? ' start-page--reveal' : ''}`}
            style={
              {
                ...(startMedia.type !== 'video' ? { backgroundImage: `url(${startMediaResolved.src})` } : null),
                ...(curtainFeelTheme || null),
              }
            }
          >
            {startMedia.type === 'video' && startMedia.src && (
              <video
                className="start-page-video-bg"
                autoPlay
                muted
                playsInline
                loop
                src={startMediaResolved.src}
              />
            )}
            {useEnvelopeFeel ? (
              <>
                <div
                  className="start-page-envelope-panel"
                  dir={startLinesLang === 'ar' ? 'rtl' : 'ltr'}
                  style={envelopeFeelTheme}
                >
                  <div className="start-page-envelope-panel__kraft" aria-hidden="true" />
                  <div
                    className="start-page-envelope-panel__flap start-page-envelope-panel__flap--left"
                    aria-hidden="true"
                  />
                  <div
                    className="start-page-envelope-panel__flap start-page-envelope-panel__flap--right"
                    aria-hidden="true"
                  />
                  <div
                    className="start-page-envelope-panel__flap start-page-envelope-panel__flap--bottom"
                    aria-hidden="true"
                  />
                  <div className="start-page-envelope-content">
                    <div className="start-page-envelope-panel__ornament" aria-hidden="true" />
                    <div className="start-page-envelope-panel__welcome">
                      {this.renderText(ui.welcome, {
                        fontFamily: "'Cormorant Garamond', 'Lora', serif",
                        fontSize: 'clamp(22px, 5.2vw, 32px)',
                        fontWeight: 600,
                      })}
                    </div>
                    {this.state.invitationTo ? (
                      <div className="start-page-envelope-panel__guest">{this.state.invitationTo}</div>
                    ) : null}
                    <div className="start-page-envelope-panel__lines">
                      {startLinesFiltered.map((line, j) => (
                        <div
                          key={`env-line-${j}`}
                          className={`start-page-envelope-panel__line${
                            j === 0 ? ' start-page-envelope-panel__line--lead' : ''
                          }`}
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="start-page-wax-seal"
                      aria-label={
                        startLinesLang === 'ar'
                          ? 'افتح الدعوة — اضغط الختم'
                          : 'Open invitation — press the wax seal'
                      }
                      onClick={this.handleStartPageOpen}
                    >
                      <span className="start-page-wax-seal__shine" aria-hidden="true" />
                      <img
                        className="start-page-wax-seal__logo"
                        src={sealLogoSrc}
                        alt=""
                        draggable={false}
                      />
                    </button>
                    <p className="start-page-envelope-panel__hint">
                      {startLinesLang === 'ar'
                        ? 'اضغط الختم الأحمر لفتح الدعوة'
                        : 'Press the  seal to open'}
                    </p>
                  </div>
                  <div
                    className="start-page-envelope-panel__flap start-page-envelope-panel__flap--top"
                    aria-hidden="true"
                  />
                </div>
                <div className="start-page-reveal-film" aria-hidden="true" />
              </>
            ) : useCurtainFeel ? (
              <>
                <div className="start-page-curtain" aria-hidden="true">
                  <div className="start-page-curtain__panel start-page-curtain__panel--left" />
                  <div className="start-page-curtain__panel start-page-curtain__panel--right" />
                </div>
                <div className="start-page-curtain-content">
                  <div className="start-page-curtain-content__welcome">
                    {this.renderText(ui.welcome, { fontFamily: "'Lora', serif", fontSize: '30px' })}
                  </div>
                  <div className="start-page-curtain-content__guest">{this.state.invitationTo}</div>
                  <button type="button" className="start-page-curtain-btn" onClick={this.handleStartPageOpen}>
                    {this.getStartLines().map((line, i) => (
                      <div key={`cstart-${i}`} style={{ fontFamily: "'Ubuntu', sans-serif, Verdana", fontSize: '30px' }}>
                        {line}
                      </div>
                    ))}
                  </button>
                </div>
                <div className="start-page-reveal-film" aria-hidden="true" />
              </>
            ) : (
              <>
                <div className="start-page-foreground">
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '30px', marginTop: '20%' }}>
                    {this.renderText(ui.welcome, { fontFamily: "'Lora', serif", fontSize: '30px' })}
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '30px' }}>{this.state.invitationTo}</div>
                </div>
                <div id="startPageBtn" onClick={() => this.startCardTour(false)}>
                  {this.getStartLines().map((line, i) => (
                    <div
                      key={`start-${i}`}
                      style={{
                        fontFamily: "'Ubuntu', sans-serif, Verdana",
                        fontSize: '30px',
                        marginTop: i === 0 ? '35px' : undefined,
                      }}
                    >
                      {line}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div
            id="SwipeableViews"
            className={`card-swipe-root${bookPageSwipeLive ? ' card-swipe-root--book' : ''}${
              scrollSinglePageLive ? ' card-swipe-root--scroll' : ''
            }`}
            style={{
              opacity: didstart ? (swipeViewsOpaque ? 1 : 0) : 0,
              transition: didstart ? 'opacity 0.7s ease' : undefined,
              ...(bookPerspectivePx != null ? { '--book-swipe-perspective': `${bookPerspectivePx}px` } : {}),
            }}
          >
            <div
              ref={this.swipeStripRef}
              className="card-swipe-strip"
              onPointerDown={this.handleStripPointerDown}
              onPointerMove={this.handleStripPointerMove}
              onPointerUp={this.handleStripPointerUp}
              onPointerCancel={this.handleStripPointerUp}
              onLostPointerCapture={this.handleStripLostPointerCapture}
            >
            <div className="card-swipe-page">
            <div id="slide1" className='swipeablePage'>
              {this.renderSlideBackground('slide1')}
              <div id="slide1Header">
                <div style={{ paddingTop: "25px" }}>
                  {this.renderPersonName('husband', { fontFamily: "'Great Vibes', cursive", fontSize: "40px" }, langSlide1.husband)}
                </div>
                {this.renderLocalizedField(amp, {}, 'ampersand-s1', langSlide1.ampersand)}
                <div>
                  {this.renderPersonName('bride', { fontFamily: "'Great Vibes', cursive", fontSize: "40px" }, langSlide1.bride)}
                </div>
                {this.renderText(slide1Copy.gettingMarried, { fontFamily: "'Lora', serif", fontSize: "22px", padding: "0 8px", marginTop: "8px" })}
              </div>
              <div id="slide1Body">
                <div style={{ fontFamily: "'Great Vibes', cursive", fontSize: "28px", fontStyle: "italic", marginTop: "auto", paddingBottom: "8px" }}>
                  {this.renderText(slide1Copy.countdownCaption, { fontFamily: "'Great Vibes', cursive", fontSize: "28px", fontStyle: "italic" })}
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: "30px", flexWrap: "wrap", paddingBottom: "12px" }}>
                  <div style={{ textAlign: "center" }}>
                    <div className="countdown-value-wrap">
                      <div style={{ fontSize: "40px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>
                        {this.getCountdownScratchLive() && !this.state.scratchRevealed.days ? '' : this.state.countdown.days}
                      </div>
                      {this.getCountdownScratchLive() ? this.renderCountdownValueScratch('days') : null}
                    </div>
                    <div style={{ fontSize: "24px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>{this.getCountdownUnitLabel('days')}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="countdown-value-wrap">
                      <div style={{ fontSize: "40px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>
                        {this.getCountdownScratchLive() && !this.state.scratchRevealed.hours ? '' : this.state.countdown.hours}
                      </div>
                      {this.getCountdownScratchLive() ? this.renderCountdownValueScratch('hours') : null}
                    </div>
                    <div style={{ fontSize: "24px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>{this.getCountdownUnitLabel('hours')}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="countdown-value-wrap">
                      <div style={{ fontSize: "40px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>
                        {this.getCountdownScratchLive() && !this.state.scratchRevealed.minutes ? '' : this.state.countdown.minutes}
                      </div>
                      {this.getCountdownScratchLive() ? this.renderCountdownValueScratch('minutes') : null}
                    </div>
                    <div style={{ fontSize: "24px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>{this.getCountdownUnitLabel('minutes')}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div className="countdown-value-wrap">
                      <div style={{ fontSize: "40px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>
                        {this.getCountdownScratchLive() && !this.state.scratchRevealed.seconds ? '' : this.state.countdown.seconds}
                      </div>
                      {this.getCountdownScratchLive() ? this.renderCountdownValueScratch('seconds') : null}
                    </div>
                    <div style={{ fontSize: "24px", fontFamily: "'Great Vibes', cursive", fontStyle: "italic" }}>{this.getCountdownUnitLabel('seconds')}</div>
                  </div>
                </div>
              </div>
              <div id="slide1Footer">
                <div id="muteUnmute" onClick={this.handleMute}>
                  {
                    isVideoMuted ?
                      <>&#128263;</>
                      :
                      <>&#128264;</>
                  }
                </div>
                <div className={`arrow${scrollSinglePageLive ? ' arrow--scroll-hint' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

                <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}>{this.getSwipeNavFooterText()}</div>
              </div>
            </div>
            </div>

            <div className="card-swipe-page">
            <div
              id="slide2"
              className={`swipeablePage${slide2FillPage ? ' slide2--fill-page' : ''}${slide2Compact ? ' slide2--compact' : ''}`}
            >
              {this.renderSlideBackground('slide2')}
              <div id="slide2Header">
              </div>
              <div
                id="slide2Body"
                className={`slide2-body${slide2FillPage ? ' slide2-body--fill' : ''}${slide2Compact ? ' slide2-body--compact' : ''}`}
              >
                <div className="slide2-section slide2-section--scripture">
                  <div className="slide2-scripture-wrap">
                    {this.renderText(AppData.bibleQuotes, {})}
                  </div>
                  <div className="slide2-evangelist-wrap">
                    {this.renderText(AppData.evangelist, {})}
                  </div>
                </div>
                <div
                  className="slide2-section slide2-section--invite"
                  dir={mqDisplayLang === 'ar' ? 'rtl' : 'ltr'}
                >
                  {this.renderMarriageQuotes('slide2-invite-line')}
                </div>
                <div className="slide2-section slide2-section--couple">
                  <div
                    className={
                      langSlide2.husband === 'ar' ? 'slide2-couple-name slide2-couple-name--ar' : 'slide2-couple-name slide2-couple-name--en'
                    }
                  >
                    {this.renderPersonName('husband', this.slide2CoupleTextStyle(langSlide2.husband), langSlide2.husband)}
                  </div>
                  <div
                    className={
                      langSlide2.ampersand === 'ar' ? 'slide2-couple-name slide2-couple-name--ar' : 'slide2-couple-name slide2-couple-name--en'
                    }
                  >
                    {this.renderLocalizedField(amp, this.slide2AmpersandTextStyle(langSlide2.ampersand), 'ampersand-s2', langSlide2.ampersand)}
                  </div>
                  <div
                    className={
                      langSlide2.bride === 'ar' ? 'slide2-couple-name slide2-couple-name--ar' : 'slide2-couple-name slide2-couple-name--en'
                    }
                  >
                    {this.renderPersonName(
                      'bride',
                      { ...this.slide2CoupleTextStyle(langSlide2.bride), paddingTop: '2px' },
                      langSlide2.bride
                    )}
                  </div>
                </div>
                <div className="slide2-section slide2-section--ceremony">
                  {this.renderSlide2CeremonyBlock()}
                </div>
              </div>
              <div id="slide2Footer">
                <div id="muteUnmute" onClick={this.handleMute}>
                  {
                    isVideoMuted ?
                      <>&#128263;</>
                      :
                      <>&#128264;</>
                  }
                </div>
                <div className={`arrow${scrollSinglePageLive ? ' arrow--scroll-hint' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div>{this.getSwipeNavFooterText()}</div>
              </div>
            </div>
            </div>

            <div className="card-swipe-page">
            <div id="slide3" className='swipeablePage'>
              {this.renderSlideBackground('slide3')}
              <div id="slide3Header">
                <div style={{ paddingTop: "20px" }}>{this.renderText(ceremony.title, { paddingTop: "20px" })}</div>
              </div>
              <div id="slide3Body">
                <div >
                  <div style={{ height: "50px" }} />
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "55px", fontWeight: "bold" }}>
                    {this.renderText(ceremony.dateLine, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "55px", fontWeight: "bold" })}
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" }}>
                    {this.renderText(ceremony.time, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" })}
                  </div>
                </div>

                <div >
                  <div style={{ fontSize: "35px", marginTop: "50px" }}>	&#128205; </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px", fontWeight: "bold" }}>
                    {this.renderText(ceremony.church, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px", fontWeight: "bold" })}
                  </div>
                  {biHasContent(ceremony.churchNote) && (
                    <div style={{ fontFamily: "'Lora', serif", fontSize: "18px", marginTop: "8px" }}>
                      {this.renderText(ceremony.churchNote, { fontFamily: "'Lora', serif", fontSize: "18px", marginTop: "8px" })}
                    </div>
                  )}
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" }}>
                    {this.renderText(ceremony.location, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" })}
                  </div>
                </div>
                <div style={{ margin: "20px" }}>
                  <div style={{ margin: "20px" }}>
                    <div style={{ padding: "10px", border: "#fff solid 1px", width: "100px", borderRadius: "10px", margin: "auto" }}><a href={ceremony.googleMap} target="_blank" rel="noopener noreferrer" style={{ color: "white" }}>{this.getUiLine(ui.locationMap)}</a></div>
                  </div>
                </div>

                {this.renderCalendarAddBlock('ceremony')}

              </div>
              <div id="slide3Footer">
                <div id="muteUnmute" onClick={this.handleMute}>
                  {
                    isVideoMuted ?
                      <>&#128263;</>
                      :
                      <>&#128264;</>
                  }
                </div>
                <div className={`arrow${scrollSinglePageLive ? ' arrow--scroll-hint' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

                <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}>{this.getSwipeNavFooterText()}</div>
              </div>
            </div>
            </div>

            {AppData.dinner && biHasContent(AppData.dinner.dinnerTitle) &&
            <div className="card-swipe-page">
              <div id="slide4" className='swipeablePage'>
                {this.renderSlideBackground('slide4')}
                <div id="slide4Header">
                  <div style={{ paddingTop: "20px" }}>{this.renderText(AppData.dinner.dinnerTitle, { paddingTop: "20px" })}</div>
                </div>
                <div id="slide4Body">
                  <div >
                    <div style={{ height: "50px" }} />
                    <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "40px", fontWeight: "bold" }}>
                      {this.renderText(AppData.dinner.dinnerDate, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "40px", fontWeight: "bold" })}
                    </div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" }}>
                      {this.renderText(AppData.dinner.dinnerTime, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" })}
                    </div>
                  </div>

                  <div >
                    <div style={{ fontSize: "35px", marginTop: "50px" }}>	&#128205;</div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px", fontWeight: "bold" }}>
                      {this.renderText(AppData.dinner.dinnerResto, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px", fontWeight: "bold" })}
                    </div>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" }}>
                      {this.renderText(AppData.dinner.dinnerLocation, { fontFamily: "'Lora', serif", fontSize: "20px", marginTop: "10px" })}
                    </div>
                  </div>

                  <div style={{ margin: "20px" }}>
                    <div style={{ padding: "10px", border: "#fff solid 1px", width: "100px", borderRadius: "10px", margin: "auto" }}><a href={AppData.dinner.dinnerGoogleMap} target="_blank" rel="noopener noreferrer" style={{ color: "white" }}>{this.getUiLine(ui.locationMap)}</a></div>
                  </div>
                </div>
                {this.renderCalendarAddBlock('dinner')}
                <div id="slide4Footer">
                  <div id="muteUnmute" onClick={this.handleMute}>
                    {
                      isVideoMuted ?
                        <>&#128263;</>
                        :
                        <>&#128264;</>
                    }
                  </div>
                  <div className={`arrow${scrollSinglePageLive ? ' arrow--scroll-hint' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>

                  <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}>{this.getSwipeNavFooterText()}</div>
                </div>
              </div>
            </div>
            }

            {AppData.gift && biHasContent(AppData.gift.giftRegistryTitle) &&
            <div className="card-swipe-page">
              <div id="slide5" className='swipeablePage'>
                {this.renderSlideBackground('slide5')}
                <div id="slide5Header">
                  <div style={{ paddingTop: "20px" }}>{this.renderText(AppData.gift.giftRegistryTitle, { paddingTop: "20px" })}</div>
                </div>
                <div id="slide5Body">
                  <div style={{ paddingTop: "24px" }} />
                  {(AppData.gift.lines || []).map((line, idx) => (
                    <div key={`gift-line-${idx}`} style={{ fontFamily: "'Lora', serif", fontSize: "21px", padding: "6px 15px" }}>
                      {this.renderText(line, { fontFamily: "'Lora', serif", fontSize: "21px" })}
                    </div>
                  ))}
                  <div
                    style={{
                      backgroundImage: `url(${this.mediaUrl(AppData.gift.brandLogoSrc || '/img/image01.png')})`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      height: "35px",
                      width: "100px",
                      marginLeft: "calc(50% - 50px)",
                      marginTop: "8px",
                    }}
                  />
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "23px" }}>
                    {this.resolveText(AppData.gift.accountLabel)}{AppData.gift.giftRegistryID}
                  </div>
                </div>
                <div id="slide5Footer">
                  <div id="muteUnmute" onClick={this.handleMute}>
                    {
                      isVideoMuted ?
                        <>&#128263;</>
                        :
                        <>&#128264;</>
                    }
                  </div>
                  <div className={`arrow${scrollSinglePageLive ? ' arrow--scroll-hint' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}>{this.getSwipeNavFooterText()}</div>
                </div>
              </div>
            </div>
            }

            {AppData.rsvp && biHasContent(AppData.rsvp.rsvpTitle) &&
            <div className="card-swipe-page">
              <div id="slide6" className='swipeablePage'>
                {this.renderSlideBackground('slide6')}
                <div id="slide6Header">
                  <div style={{ paddingTop: "20px" }}>{this.renderText(AppData.rsvp.rsvpTitle, { paddingTop: "20px" })}</div>
                </div>
                <div id="slide6Body">
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "22px", padding: "20px 15px 10px 15px" }}>
                    {this.renderText(AppData.rsvp.rsvpReplyDate, { fontFamily: "'Lora', serif", fontSize: "22px", padding: "20px 15px 10px 15px" })}
                  </div>
                  {/* <div style={{ fontFamily: "'Lora', serif", fontSize: "16px", fontStyle: "italic" }}>Number Of Invitees:&nbsp;&nbsp;{this.state.nbrOfInvited}</div> */}

                  <div className="guest-list-container">
                    {this.state.listOfInv.map((person, index) => (
                      <div key={index} className="guest-row">
                        <span className="guest-name">{person.name}</span>
                        <div className="rsvp-buttons">
                          <button
                            className={`rsvp-button accept ${person.status === 'A' ? 'selected' : ''}`}
                            onClick={() => this.handleRsvpStatusChange(index, 'A')}
                          >
                            {this.resolveText((ui.rsvp || {}).accept)}
                          </button>
                          <button
                            className={`rsvp-button decline ${person.status === 'D' ? 'selected' : ''}`}
                            onClick={() => this.handleRsvpStatusChange(index, 'D')}
                          >
                            {this.resolveText((ui.rsvp || {}).decline)}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button className='attendingPart' style={{ marginTop: "20px" }} onClick={this.reserve} >{this.resolveText((ui.rsvp || {}).submit)}</button>
                </div>
                {/* <div id="slide6Footer">
                  <div id="muteUnmute" onClick={this.handleMute}>
                    {
                      isVideoMuted ?
                        <>&#128263;</>
                        :
                        <>&#128263;</>
                    }
                  </div>
                  <div className="arrow">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}>SWIPE LEFT</div>

                </div> */}
              </div>
            </div>
            }
            {/* {AppData.thanksQuote &&
              <div id="slide7" className='swipeablePage'>
                <div id="slide7Header">
                </div>
                <div id="slide7Body">
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "21px", padding: "60px 15px 30px 15px" }}>{AppData.thanksQuote}</div>
                </div>
                <div id="slide7Footer">
                  <div id="muteUnmute" onClick={this.handleMute}>
                    {
                      isVideoMuted ?
                        <>&#128263;</>
                        :
                        <>&#128263;</>
                    }
                  </div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: "11px" }}></div>
                </div>
              </div>
            } */}
            </div>
          </div>

        </div>
        <div
          id="lastPage"
          onPointerDown={lastFw.tapEnabled ? this.handleLastPageTapFirework : undefined}
          style={
            this.state.displayLastPage === true
              ? {
                  display: 'block',
                  height: '100vh',
                  position: 'relative',
                  overflow: 'hidden',
                  touchAction: 'manipulation',
                  cursor: lastFw.tapEnabled ? 'pointer' : 'default',
                }
              : { display: 'none' }
          }
        >
          <div
            className="lastPage-scrim"
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              backgroundColor: 'rgba(0,0,0,0.55)',
              pointerEvents: 'none',
            }}
          />
          <div
            className="lastPage-ambient-fireworks"
            aria-hidden="true"
            style={{ filter: `brightness(${lastPageFireworksBrightness})`, zIndex: 1 }}
          >
            <div className="firework firework--ambient firework--p1" />
            <div className="firework firework--ambient firework--p2" />
            <div className="firework firework--ambient firework--p3" />
            <div className="firework firework--ambient firework--p4" />
            <div className="firework firework--ambient firework--p5" />
            <div className="firework firework--ambient firework--p6" />
          </div>
          <div
            className="lastPage-user-fireworks"
            style={{ filter: `brightness(${lastPageFireworksBrightness})`, zIndex: 2 }}
          >
            {this.state.lastPageUserFireworks.map((f) => (
              <div
                key={f.id}
                className="firework firework--user"
                style={{
                  left: `${f.x}%`,
                  top: `${f.y}%`,
                  ['--firework-hue']: f.hue,
                }}
                onAnimationEnd={() => this.handleLastPageUserFireworkEnd(f.id)}
              />
            ))}
          </div>
          <div
            style={{
              color: 'white',
              position: 'relative',
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: '100vh',
              boxSizing: 'border-box',
              paddingTop: 'max(12px, 2vh)',
              paddingBottom: 'max(12px, 2vh)',
            }}
          >
            <div
              style={{
                backgroundImage: `url(${this.mediaUrl((ui.lastPage && ui.lastPage.logoSrc) || '/img/Logo.jpeg')})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
                height: '200px',
                width: '200px',
                maxWidth: 'min(200px, 72vw)',
              }}
            />
            {(ui.lastPage && ui.lastPage.lines ? ui.lastPage.lines : []).map((line, i) => (
              <div
                key={`last-${i}`}
                style={{
                  fontFamily: "'Lora', serif",
                  fontSize: '25px',
                  padding: '0 10px',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                {this.renderText(line, {
                  fontFamily: "'Lora', serif",
                  fontSize: '25px',
                  padding: '0 10px',
                  fontStyle: 'italic',
                  textAlign: 'center',
                })}
              </div>
            ))}
          </div>
          {showLastPageTapHint ? (
            <div
              className="lastPage-tap-hint"
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 'max(16px, 3vh)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                zIndex: 10,
                fontFamily: "'Lora', serif",
                fontSize: 'clamp(13px, 3.5vw, 15px)',
                color: '#ffffff',
                fontWeight: 500,
                letterSpacing: '0.04em',
                paddingLeft: '16px',
                paddingRight: '16px',
                textAlign: 'center',
                textShadow: '0 0 12px rgba(0,0,0,0.95), 0 2px 4px rgba(0,0,0,0.8)',
              }}
            >
              {tapHintText}
            </div>
          ) : null}
        </div>
      </>
    );
  }
}

export default MainAppContainer;